import { useRouter } from 'vue-router'
import { addArraySyntax } from '@/utils/helpers'
import { scrollToElemTop } from '@/utils/scroll-to'
import { focusSpaTitle } from '@/utils/head'
import { useSearchStore } from '@/stores/search'
import { ref } from 'vue'
import type { Ref } from 'vue'
import { bp } from '@/constants'

/**
 * Handles search route param updates and scroll / focus management for filter SPAs.
 */
export function useSearchRoute() {
  const router = useRouter()
  const searchStore = useSearchStore()
  const contentResultsEl = ref<HTMLElement | null>(null)
  const contentOrgEl = ref<HTMLElement | null>(null)

  /**
   * Manually handle page scrolling and focus, NOT handled by vue-router
   */
  function scrollAndFocus({
    scrollTo,
    focusTitle,
  }: {
    scrollTo: Ref<HTMLElement | null>
    focusTitle?: boolean
  }) {
    if (!scrollTo.value) {
      return
    }

    scrollToElemTop({
      elem: scrollTo.value,
      ms: 250,
      _smoothScrollCallback: () => {
        const spaTitle = document.querySelector('#spa-title')

        if (focusTitle && spaTitle instanceof HTMLElement) {
          focusSpaTitle(spaTitle, {
            preventScroll: true,
          })
        }
      },
    })
  }

  /**
   * Manually update route params and handle page scrolling / focusing
   */
  async function routeUpdate(customParams?: Record<string, string | string[]>) {
    // pass in customParams: {} to clear out all filters
    const params = customParams ? customParams : searchStore.apiParameters

    // 99% of the time this is a NavigationDuplicate error (same page + diff query string) and floods sentry
    // @see https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
    return router
      .push({ query: addArraySyntax(params), params: { noScroll: 'noScroll' } })
      .catch(() => {})
  }

  /**
   * Reset, scroll to top and focus title
   */
  async function clearAll() {
    await routeUpdate({})
    scrollAndFocus({ scrollTo: contentOrgEl, focusTitle: true })
  }

  /**
   * No scroll or focus
   */
  function tagUpdate() {
    routeUpdate()
  }

  /**
   * Scroll only, no focus
   */
  async function filterUpdate() {
    await routeUpdate()

    // only scroll and focus if filters if desktop mode
    // this caused issues with iOS 17 where clicking checkbox items closer to the top
    // stopped working when .filters-open was on the body
    if (window.innerWidth > bp.headerTablet) {
      scrollAndFocus({ scrollTo: contentResultsEl, focusTitle: false })
    }
  }

  async function sortUpdate() {
    await routeUpdate()
    scrollAndFocus({ scrollTo: contentResultsEl, focusTitle: false })
  }

  /**
   * Pagination helper, scroll and focus
   */
  async function pageUpdate(page: number) {
    const paramsWithPagination = { ...searchStore.apiParameters }

    // force string for payload (and only add if beyond page 1)
    if (page !== 1) {
      paramsWithPagination.page = `${page}`
    }

    await routeUpdate(paramsWithPagination)
    scrollAndFocus({ scrollTo: contentResultsEl, focusTitle: true })
  }

  return {
    contentResultsEl,
    contentOrgEl,
    scrollAndFocus,
    routeUpdate,
    clearAll,
    tagUpdate,
    filterUpdate,
    sortUpdate,
    pageUpdate,
  }
}
